import React, { useEffect, useRef, useState } from 'react';
import { Stack, Typography, Box, Alert, Snackbar } from '@mui/material';
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import mainLogo from '../../images/black_letters_with_blue_shadow.svg';
import { QRCodeSVG } from 'qrcode.react';
import { useUserApi } from '../../api/user';
import { AnimationLoadingScreenWithProgressBar } from '../../components/loading/loadingScreen';

import './thankyou.css';

export const ThankYouGuestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useUserApi();

  const [ email, setEmail ] = useState( '' );
  const [ assessmentURL, setAssessmentURL ] = useState( '' );
  const [ loading, setLoading ] = useState( true );
  const [ error, setError ] = useState( null );
  const [ emailStatus, setEmailStatus ] = useState( { open: false, message: '', severity: 'info' } );

  const hasFetched = useRef( false );

  useEffect( () => {
    const emailFromState = location.state?.email;
    const firstNameFromState = location.state?.firstName || 'User';
    setEmail( emailFromState );

    if ( !hasFetched.current && emailFromState ) {
      hasFetched.current = true;

      const initializeThankYouPage = async () => {
        try {
          setLoading( true );
          const userData = {
            email: emailFromState,
            first_name: firstNameFromState,
          };

          // Get the assessment URL
          const urlResponse = await api.getAssessmentResultsURL( userData );
          if ( !urlResponse?.assessmenturl ) {
            throw new Error( 'Invalid URL response format' );
          }
          setAssessmentURL( urlResponse.assessmenturl );

          // Register and send the email
          const emailResponse = await api.registerAssessmentResults( userData );
          if ( emailResponse?.status === 'queued' ) {
            setEmailStatus( {
              open: true,
              message: 'Email has been sent successfully!',
              severity: 'success'
            } );
          }

        } catch ( err ) {
          console.error( 'Error in thank you page initialization:', err );
          if ( err.message.includes( 'URL' ) ) {
            setError( 'Failed to load the assessment URL.' );
          } else {
            setEmailStatus( {
              open: true,
              message: 'Failed to send email. Please try again later.',
              severity: 'error'
            } );
          }
        } finally {
          setLoading( false );
        }
      };

      initializeThankYouPage();
    } else if ( !emailFromState ) {
      setLoading( false );
      setError( 'No email provided' );
    }
  }, [ location.state, api, navigate ] );

  const handleSnackbarClose = ( event, reason ) => {
    if ( reason === 'clickaway' ) {
      return;
    }
    setEmailStatus( { ...emailStatus, open: false } );
  };

  const handleResendEmail = async () => {
    try {
      setEmailStatus( { open: true, message: 'Sending email...', severity: 'info' } );
      const userData = {
        email: email,
        first_name: location.state?.firstName || 'User',
      };
      const response = await api.registerAssessmentResults( userData );

      if ( response?.status === 'queued' ) {
        setEmailStatus( {
          open: true,
          message: 'Email has been resent successfully!',
          severity: 'success'
        } );
      }
    } catch ( err ) {
      console.error( 'Error resending email:', err );
      setEmailStatus( {
        open: true,
        message: 'Failed to resend email. Please try again.',
        severity: 'error'
      } );
    }
  };

  if ( loading ) {
    return (
      <AnimationLoadingScreenWithProgressBar
        title="Loading..."
        secondsToLoad={ 15 }
      />
    );
  }

  if ( error ) {
    return (
      <Box className="thank-page-wrapper">
        <Stack className="thankyou-card">
          <Typography color="error" textAlign="center">
            { error }
          </Typography>
          <Link to="/guest-demographics" className="continue-btn">
            Start New Assessment
          </Link>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="thank-page-wrapper">
      <Snackbar
        open={ emailStatus.open }
        autoHideDuration={ 6000 }
        onClose={ handleSnackbarClose }
        anchorOrigin={ { vertical: 'top', horizontal: 'center' } }
      >
        <Alert onClose={ handleSnackbarClose } severity={ emailStatus.severity }>
          { emailStatus.message }
        </Alert>
      </Snackbar>

      <Stack className="thankyou-card">
        <Box className="card-header" textAlign="center">
          <img src={ mainLogo } alt="Logo" height={ 80 } className="aimd-logo" />
        </Box>

        <Box className="card-body">
          <Stack className="thankyou-page bg-light" minHeight="340px">
            <Stack className="card-info" textAlign="center" marginTop="1rem" minHeight='400px' display='flex' alignItems='center' justifyContent='center'>
              <Typography variant="h4" component="h4">
                Thank you!
              </Typography>
              { assessmentURL && (
                <div>
                  <Typography variant="body1" component="p" className="info-txt" marginTop={ 1 } marginBottom={ 1 }>
                    Scan this code to see your health recommendations:
                  </Typography>
                  <QRCodeSVG value={ assessmentURL } size={ 128 } />
                </div>
              ) }
              <div>
                <a
                  href={ assessmentURL }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={ { textDecoration: 'underline', color: '#0073e6', marginTop: '1rem', display: 'block' } }
                >
                  Click here to view your results
                </a>
              </div>
              <Typography variant="body1" component="p" className="info-txt" marginTop={ 1 }>
                <br />Your health recommendations have been sent to your email. <br /><br />
                If you haven't received the email, check your spam folder or
                <button
                  onClick={ handleResendEmail }
                  style={ {
                    background: 'none',
                    border: 'none',
                    color: '#0073e6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: '0 4px'
                  } }
                >
                  click here to resend
                </button>.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack display="flex" flexDirection="column" justifyContent="center" alignItems="center" marginTop={ 1 }>
          <Link to="/guest-demographics" className="continue-btn">
            Start A New Assessment
          </Link>
        </Stack>
      </Stack>
    </Box>
  );
};